import { Controller } from '@hotwired/stimulus';
import { Helpers } from '../helpers';
import * as bootstrap from 'bootstrap';
import { embedSignature, onSignliftEvent, finalizeSignature, SIGNLIFT_EVENTS } from '../lib/signlift';
import { subscribeToIntention, subscribeToSignature } from '../lib/investment_channels';
import { fetchFees, validateAmount, applyWarnings, formatAmount, parseFloatSafe } from '../lib/investment_fees';

export default class InvestFormController extends Controller {
  static targets = [
    'subscriptionAmount',
    'feesAmount',
    'fieldsErrors',
    'subscriptionAmountDivs',
    'feesAmountDivs',
    'totalAmountDivs',
    'nonInformedFields',
    'modalTotalWithFees',
    'insufficientBalance',
    'subscribeModal',
    'form',
    'stepVerification',
    'stepGeneration',
    'stepSignature',
    'signatureIframe',
    'stepConfirmation',
    'stepPaymentPending',
    'paymentPendingDesc',
    'stepError',
  ];

  static values = {
    feesUrl: String,
    projectId: String,
    profileBalance: Number,
    maxInvestRecommendation: Number,
    userMayHaveWarning: Boolean,
    finalizeSubscriptionId: String,
    errorMessage: String,
    emptyAmountMessage: String,
    paymentPendingMessage: String,
  };

  connect() {
    this.finalizeUrl = null;
    this.finalizeInflight = false;
    this.submitInflight = false;
    this.cleanupSignlift = null;

    if (this.hasFinalizeSubscriptionIdValue) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.#resumeFinalization());
      });
    }
  }

  disconnect() {
    this.cleanupSignlift?.();
    this.intentionSubscription?.unsubscribe();
    this.signatureSubscription?.unsubscribe();
  }

  // ── Profile change ────────────────────────────────────────

  updateProfileBalance({detail: {balanceRaw}}) {
    this.profileBalanceValue = balanceRaw;
    this.computeFeesAndTotal();
  }

  // ── Fees & validation ─────────────────────────────────────

  async computeFeesAndTotal() {
    const amount = this.subscriptionAmountTarget.value;

    try {
      const {feesAmount, totalWithFeesAmount} =
        await fetchFees(this.feesUrlValue, amount, this.projectIdValue);

      this.#applyValidation(amount, totalWithFeesAmount);

      this.feesAmountTarget.value = feesAmount;
      this.#updateDisplayAmounts(amount, feesAmount, totalWithFeesAmount);
    } catch {
      this.fieldsErrorsTarget.innerHTML =
        this.errorMessageValue;
    }
  }

  // ── Step navigation ───────────────────────────────────────

  validateAndShowSubscriptionModal() {
    if (!this.#validateAndAdvance()) return;
    const modal = new bootstrap.Modal(this.subscribeModalTarget);
    modal.show();
  }

  validateAndGoToConfirmation() {
    if (!this.#validateAndAdvance()) return;
    this.dispatch('next', {prefix: 'modal-steps', bubbles: true});
  }

  preventAutoSubmit(event) {
    event.preventDefault();
    this.validateAndShowSubscriptionModal();
  }

  // ── Form submission ───────────────────────────────────────

  async submitAndAdvance(event) {
    event.preventDefault();
    if (!this.hasFormTarget) return;
    if (this.submitInflight) return;
    this.submitInflight = true;

    const submitter = event.submitter;
    if (submitter) submitter.disabled = true;

    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        body: new FormData(this.formTarget),
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector(
            'meta[name="csrf-token"]'
          )?.content,
        },
        credentials: 'same-origin',
      });

      const data = await response.json();

      if (!response.ok || data.status === 'error') {
        this.#showErrorStep();
        return;
      }

      this.dispatch('next', {prefix: 'modal-steps', bubbles: true});
      this.#startIntentionChannel(data.intention_id);
    } catch {
      this.#showErrorStep();
    } finally {
      this.submitInflight = false;
    }
  }

  // ── Legacy modal ──────────────────────────────────────────

  teardownModal() {
    if (!this.hasSubscribeModalTarget) return;
    const modal = bootstrap.Modal.getInstance(
      this.subscribeModalTarget
    );
    if (modal) {
      modal.hide();
      modal.dispose();
    }
  }

  // ── Private: validation ───────────────────────────────────

  #applyValidation(amount, totalWithFeesAmount) {
    this.insufficientBalanceTarget.classList.add('d-none');
    const result = validateAmount(
      amount,
      totalWithFeesAmount,
      parseFloatSafe(this.profileBalanceValue),
      this.subscriptionAmountTarget
    );

    switch (result) {
      case 'invalid_html':
        this.fieldsErrorsTarget.innerHTML =
          this.subscriptionAmountTarget.validationMessage;
        this.subscriptionAmountTarget.classList.add('is-invalid');
        return false;
      case 'empty':
        this.fieldsErrorsTarget.innerHTML =
          this.emptyAmountMessageValue;
        this.subscriptionAmountTarget.classList.add('is-invalid');
        return false;
      case 'insufficient_balance':
        this.insufficientBalanceTarget.classList.remove('d-none');
        this.fieldsErrorsTarget.innerHTML = '';
        this.subscriptionAmountTarget.classList.add('is-invalid');
        return false;
      default:
        this.fieldsErrorsTarget.innerHTML = '';
        this.subscriptionAmountTarget.classList.remove('is-invalid');
        return true;
    }
  }

  #validateAndAdvance() {
    const amount = this.subscriptionAmountTarget.value;
    const total =
      parseFloatSafe(amount) +
      parseFloatSafe(this.feesAmountTarget.value);

    if (!this.#applyValidation(amount, total)) return false;

    applyWarnings(
      amount,
      this.maxInvestRecommendationValue,
      this.userMayHaveWarningValue,
      this.nonInformedFieldsTargets
    );

    return true;
  }

  #updateDisplayAmounts(amount, feesAmount, totalWithFeesAmount) {
    const setAll = (targets, val) => {
      const text = formatAmount(val);
      targets.forEach((el) => (el.innerHTML = text));
    };

    setAll(this.subscriptionAmountDivsTargets, parseFloatSafe(amount));
    setAll(this.feesAmountDivsTargets, feesAmount);
    setAll(this.totalAmountDivsTargets, totalWithFeesAmount);

    if (this.hasModalTotalWithFeesTarget) {
      this.modalTotalWithFeesTarget.innerHTML =
        Helpers.numberToCurrency(totalWithFeesAmount, 2);
    }
  }

  // ── Private: tunnel channels ──────────────────────────────

  #startIntentionChannel(intentionId) {
    this.intentionSubscription = subscribeToIntention(intentionId, {
      onSuccess: (subscriptionId) => {
        this.#showStepTarget(this.stepGenerationTarget);
        this.#startSignatureChannel(subscriptionId);
      },
      onError: () => this.#showErrorStep(),
    });
  }

  #startSignatureChannel(subscriptionId) {
    this.signatureSubscription = subscribeToSignature(subscriptionId, {
      onSigned: ({paymentPending, missingAmountFormatted}) => {
        if (paymentPending && this.hasStepPaymentPendingTarget) {
          if (this.hasPaymentPendingDescTarget && missingAmountFormatted) {
            this.paymentPendingDescTarget.textContent =
              this.paymentPendingMessageValue.replace(
                '%{amount}', missingAmountFormatted
              );
          }
          this.#showStepTarget(this.stepPaymentPendingTarget);
        } else {
          this.#showStepTarget(this.stepConfirmationTarget);
        }
      },
      onSignatureReady: ({signingUrl, finalizeUrl}) => {
        this.finalizeUrl = finalizeUrl;
        this.#showStepTarget(this.stepSignatureTarget);
        embedSignature(this.signatureIframeTarget, signingUrl);
        this.cleanupSignlift?.();
        this.cleanupSignlift = onSignliftEvent(
          ({type}) => {
            if (type === SIGNLIFT_EVENTS.SUCCESS) {
              this.#triggerFinalizeAndConfirm();
            } else if (type === SIGNLIFT_EVENTS.ERROR) {
              this.#showErrorStep();
            }
          },
          signingUrl,
        );
      },
      onError: () => this.#showErrorStep(),
    });
  }

  #resumeFinalization() {
    this.#showStepTarget(this.stepGenerationTarget);
    this.#startSignatureChannel(this.finalizeSubscriptionIdValue);
  }

  async #triggerFinalizeAndConfirm() {
    this.#showStepTarget(this.stepConfirmationTarget);

    if (!this.finalizeUrl || this.finalizeInflight) return;
    this.finalizeInflight = true;

    await finalizeSignature(this.finalizeUrl);
  }

  // ── Private: step display ─────────────────────────────────

  #showErrorStep() {
    if (!this.hasStepErrorTarget) return;
    this.#showStepTarget(this.stepErrorTarget);
  }

  #showStepTarget(target) {
    if (!target) return;
    const steps = this.element.querySelectorAll(
      '[data-modal-steps-target="step"]'
    );

    steps.forEach((s) => {
      s.style.display = 'none';
      s.classList.add('d-none');
      s.removeAttribute('style');
    });

    target.style.display = '';
    target.classList.remove('d-none');
  }
}
